import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Alert,
  Snackbar,
} from '@mui/material';
import { OnboardingLanding } from './OnboardingLanding';
import { OnboardingProviders } from './OnboardingProviders';
import { getCurrentOnboardingStep, getCurrentOnboardingStepFromMeta, OnboardingStep } from './onboardingUtils';
import { useSetBrowserTrustMutation, useGetOnboardingStatusQuery, useAcknowledgeClearVerificationMutation, healthWalletApi } from './healthWalletSlice';
import type { OnboardingStatus } from './healthWalletSlice';
import type { ClearStatus } from '../../types/onboarding';
import { useAppDispatch } from '../../app/hooks';
import { AppLayout } from '../../App';
//import { markOnboardingComplete } from './clearTokenManager';
import { OnboardingTrustBrowser } from './OnboardingTrustBrowser';
import { OnboardingVerification } from './OnboardingVerification';
import { OnboardingConsent } from './OnboardingConsent';
import { WalletLoadingScreen } from './WalletLoadingScreen';
import { LoadingScreen } from '../../LoadingScreen';
import { printError } from '../../app/utils';
import { ReVerification } from './ReVerification.';
import { useLogToBackEndMutation } from '../../features/utilitySlice';
import { getVersion, useAuthProfile } from '../../app/hooks';

// Steps renderStep() actually draws. Any other step reaching it renders NOTHING (a blank page).
const RENDERED_STEPS: ReadonlySet<OnboardingStep> = new Set<OnboardingStep>(
  ['logo', 'landing', 'consent', 'verification', 're-verification', 'trust', 'connect', 'waiting']);

interface OnboardingFlowProps {
  onComplete: () => void;
  onReturningUser: () => void;
}

export const OnboardingFlow: FC<OnboardingFlowProps> = ({ onComplete, onReturningUser }) => {
  const [step, setStep] = useState<OnboardingStep>('logo');
  const stepRef = useRef<OnboardingStep | undefined>(undefined);
  const [setBrowserTrust] = useSetBrowserTrustMutation();
  const [acknowledgeClearVerification] = useAcknowledgeClearVerificationMutation();
  const dispatch = useAppDispatch();

  // Get onboarding status and loading state
  const OnboardingStatusQueryMeta = useGetOnboardingStatusQuery();
  const { data: onboardingStatus, isLoading, isError, error } = OnboardingStatusQueryMeta;

  // Outcome of the just-completed CLEAR exchange, server-derived (in-flow marker
  // match on /onboarding/status). Drives which phase the verification screens
  // render in. Lives at component scope (not inside renderStep) so the
  // failure-acknowledge effect below can watch it.
  const clearVerifiedInFlow = onboardingStatus?.clearVerifiedInFlow ?? null;
  const clearPhase: 'success' | 'error' | null =
    clearVerifiedInFlow === 'success' ? 'success'
      : clearVerifiedInFlow && clearVerifiedInFlow !== 'never_set' ? 'error'
      : null;

  // Acknowledgment timing differs by outcome — deliberate asymmetry:
  //  - SUCCESS: acknowledged on Continue (handleVerificationNext). The marker
  //    survives until the click, so a reload mid-screen RE-SHOWS the ack screen.
  //  - FAILURE: acknowledged on render (here). The error screen's only action is
  //    retry, so "seen = acknowledged" — without this, a user who closes the
  //    browser on the error screen would see the stale error again next session
  //    instead of the normal re-verification intro.
  useEffect(() => {
    if (clearPhase === 'error') {
      acknowledgeClearVerification();
    }
  }, [clearPhase, acknowledgeClearVerification]);

  // Blank-page beacon: when the step has no screen here, report it to the server log (client.log),
  // once per step per page load. Flags only. A patient went blank 2026-10-09 (trusted browser
  // re-CLEAR → step 'data' while HealthWalletMain held this component) and prod logged nothing.
  const [logToBackEnd] = useLogToBackEndMutation();
  const { profile } = useAuthProfile();
  const reportedBlankRef = useRef<OnboardingStep | null>(null);
  useEffect(() => {
    if (!onboardingStatus || RENDERED_STEPS.has(step) || reportedBlankRef.current === step) return;
    reportedBlankRef.current = step;
    logToBackEnd({
      context: 'healthwallet.onboarding_blank_step',
      message: JSON.stringify({
        step,
        profileId: profile?.id ?? null,
        isOnboarded: onboardingStatus.isOnboarded,
        verificationStatus: onboardingStatus.verificationStatus,
        browserTrusted: onboardingStatus.browserTrusted,
        clearVerifiedInFlow: onboardingStatus.clearVerifiedInFlow ?? null,
        clearVerificationAcknowledged: onboardingStatus.clearVerificationAcknowledged ?? null,
        dataRequestStatus: onboardingStatus.dataRequestStatus ?? null,
      }),
      version: getVersion(),
    });
  }, [step, onboardingStatus, profile?.id, logToBackEnd]);

  // Helper to update both state and ref
  const updateStep = useCallback((newStep: OnboardingStep) => {
    setStep(newStep);
    stepRef.current = newStep;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (onboardingStatus) {
      const currentStep = getCurrentOnboardingStepFromMeta(OnboardingStatusQueryMeta);
      updateStep(currentStep);
    }
  }, [onboardingStatus, onComplete, updateStep]);

  const handleErrorClose = () => {
    // Clear statusError by refetching or handle as needed
  };

  const safeNavigate = useCallback((nextStep: OnboardingStep) => {
    try {
      updateStep(nextStep);
    } catch (err) {
      console.error('Navigation error in onboarding flow:', err);
      // Handle error as needed - statusError will be set by RTK Query if needed
    }
  }, [updateStep]);

  const handleNewUser = () => safeNavigate('consent');
  //Backend status now drives all forward navigation via the useEffect, eliminating duplicate API calls and navigation conflicts.
  const handleConsentNext = () => {
    // safeNavigate('verification'); // Removed: Backend status drives forward navigation via useEffect
    // When consent is submitted, backend updates status and useEffect automatically navigates to next step
  };
  const handleConsentBack = () => safeNavigate('landing');

  const handleVerificationNext = () => {
    // Success-screen dismissal (Continue / 5s auto-advance): clear the server-side
    // in-flow marker now. Fire-and-forget — the cache update below dismisses the
    // screen immediately; the server call just stops future /status calls from
    // re-delivering 'success'.
    acknowledgeClearVerification();
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        draft.clearVerifiedInFlow = null;
        draft.clearVerificationAcknowledged = true;
      })
    );
  };
  const handleVerificationBack = () => safeNavigate('consent');

  const handleTrustNext = async () => {
    // Removed: API call and safeNavigate - both handled by OnboardingTrustBrowser component
    // OnboardingTrustBrowser calls setBrowserTrust with user's choice
    // Backend status update triggers useEffect to navigate to next step
    // safeNavigate('data'); // Removed: Backend status drives forward navigation via useEffect
  };
  const handleTrustBack = () => safeNavigate('verification');

  const handleDataNext = () => {
    // Mark onboarding complete locally
    //markOnboardingComplete();

    // Cache invalidation in healthWalletSlice will automatically trigger:
    // - Onboarding status refetch (due to ['Onboarding'] tag)
    // - Dashboard query refetch (due to ['Patient'] tag)

    onComplete();
  };

  const renderStep = () => {
    // clearVerifiedInFlow / clearPhase are computed at component scope above
    // (hoisted so the failure-acknowledge effect can watch clearPhase).
    switch (step) {
      case 'logo':
        return <LoadingScreen secondLine="Health Wallet" />;
      case 'landing':
        return (
          <OnboardingLanding
            onNewUser={handleNewUser}
          />
        );
      case 'consent':
        return (
          <OnboardingConsent
            onNext={handleConsentNext}
            onBack={handleConsentBack}
          />
        );
      case 'verification':
        return (
          <OnboardingVerification
            key={clearPhase ?? 'intro'}
            onNext={handleVerificationNext}
            onBack={handleVerificationBack}
            phase={clearPhase}
            clearStatus={clearVerifiedInFlow ?? undefined}
          />
        );
      case 're-verification':
        return (
          <ReVerification
            key={clearPhase ?? 'intro'}
            onNext={handleVerificationNext}
            phase={clearPhase}
            clearStatus={clearVerifiedInFlow ?? undefined}
          />
        );
      case 'trust':
        return (
          <OnboardingTrustBrowser
            onNext={handleTrustNext}
            onBack={handleTrustBack}
          />
        );
      case 'connect':
        // "Find my providers": runs discovery on entry, surfaces Epic (Connect/Skip) + Kno2 (auto) orgs,
        // and fires the batched retrieval via "Get my records" → advances to 'waiting'. Self-contained
        // (reads /onboarding/status, drives navigation via the optimistic dataRequestStatus flip).
        return <OnboardingProviders />;
      case 'waiting':
        return (
          <WalletLoadingScreen
            onContinue={handleDataNext}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <Box role="main" aria-label="Health Wallet onboarding" sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {renderStep()}

        {/* Global error snackbar for the onboarding flow */}
        <Snackbar
          open={!!isError || !!error}
          autoHideDuration={18000}
          onClose={handleErrorClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
            {isError ? 'Failed to load onboarding status' : onboardingStatus?.error || 'An error occurred'}
          </Alert>
        </Snackbar>
      </Box>
    </AppLayout>
  );
};
